import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  Button,
  Stack,
  keyframes,
} from '@mui/material';
import {
  School,
  Business,
  Favorite,
  Groups,
  Timeline,
  EmojiEvents,
  ArrowForward,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import PublicLayout from '@/layouts/PublicLayout';

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const partnershipTypes = [
  {
    title: 'Educational Institutions',
    description: 'Partner with us to provide your students with meaningful volunteer opportunities and track their community service hours.',
    icon: School,
    color: '#2196f3',
    benefits: ['Access to volunteer management platform', 'Student progress tracking', 'Impact reporting'],
  },
  {
    title: 'Corporate Partners',
    description: 'Enhance your corporate social responsibility initiatives and engage employees in meaningful community service.',
    icon: Business,
    color: '#4caf50',
    benefits: ['Employee volunteering programs', 'CSR impact metrics', 'Community engagement'],
  },
  {
    title: 'Non-Profit Organizations',
    description: 'Join our network of non-profits to connect with passionate volunteers and expand your impact.',
    icon: Favorite,
    color: '#f44336',
    benefits: ['Volunteer recruitment', 'Hour tracking & verification', 'Impact visualization'],
  },
];

const PartnershipsPage = () => {
  const navigate = useNavigate();

  const handlePartnershipClick = () => {
    navigate('/contact?reason=business_partnership');
  };

  return (
    <PublicLayout hasDarkHero>
      {/* Hero Section */}
      <Box sx={{
        width: '100%',
        minHeight: { xs: '100vh', md: '80vh' },
        display: 'flex',
        alignItems: 'center',
        background: 'linear-gradient(to bottom, #6B46C1 0%, #4C51BF 100%)',
        position: 'relative',
        overflow: 'hidden',
        pt: { xs: '100px', md: 10, xl: 12 },
        pb: { xs: 8, md: 6, xl: 12 },
        px: { xs: 2, md: 0 },
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at 20% 150%, rgba(76, 81, 191, 0.4) 0%, rgba(107, 70, 193, 0) 50%)',
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'url("/patterns/dots.svg")',
          opacity: 0.1,
          animation: `${fadeIn} 2s ease-out`,
        }
      }}>
        <Container maxWidth="xl" sx={{ 
          position: 'relative',
          zIndex: 1,
          px: { xs: 2, sm: 3, md: 4, xl: 8 },
        }}>
          <Box sx={{ 
            maxWidth: '800px', 
            mx: 'auto',
            textAlign: 'center',
            animation: `${fadeIn} 1s ease-out`,
          }}>
            {/* <Typography 
              component="span" 
              sx={{ 
                bgcolor: 'rgba(255,255,255,0.1)',
                px: 3,
                py: 1,
                borderRadius: '50px',
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                mb: { xs: 2, sm: 3 },
                display: 'inline-block',
                color: 'rgba(255,255,255,0.9)',
                letterSpacing: '1px',
                textTransform: 'uppercase',
                fontWeight: 600,
                backdropFilter: 'blur(10px)',
              }}
            >
              Join Our Network
            </Typography> */}
            <Typography 
              variant="h1" 
              sx={{ 
                fontSize: { xs: '2rem', sm: '2.5rem', md: '4rem' },
                fontWeight: 800,
                mb: { xs: 2, sm: 3 },
                lineHeight: { xs: 1.2, md: 1.1 },
                background: 'linear-gradient(to right, #fff, rgba(255,255,255,0.8))',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                textShadow: '0 20px 30px rgba(0,0,0,0.15)',
              }}
            >
              Partner With Us to Make a Difference
            </Typography>
            <Typography
              variant="h5"
              sx={{
                color: 'rgba(255,255,255,0.9)',
                mb: { xs: 4, sm: 6 },
                maxWidth: '600px',
                mx: 'auto',
                lineHeight: 1.6,
                fontSize: { xs: '1rem', sm: '1.1rem', md: '1.25rem' },
                opacity: 0.9,
                px: { xs: 2, sm: 0 },
              }}
            >
              Join our mission to empower students through meaningful community service and shape the future of education
            </Typography>
            
            <Stack 
              direction={{ xs: 'column', sm: 'row' }}
              spacing={{ xs: 2, sm: 4 }}
              justifyContent="center"
              sx={{ 
                mb: { xs: 4, sm: 6 },
                width: '100%',
                '& > *': {
                  width: { xs: '100%', sm: 'auto' }
                }
              }}
            >
              {[
                { icon: Groups, text: 'Community Impact', color: '#64B5F6' },
                { icon: Timeline, text: 'Measurable Results', color: '#81C784' },
                { icon: EmojiEvents, text: 'Student Success', color: '#FFB74D' }
              ].map((item, index) => (
                <Box
                  key={index}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 2,
                    color: 'white',
                    transition: 'all 0.3s ease',
                    p: { xs: 1.5, sm: 1 },
                    borderRadius: 2,
                    bgcolor: { xs: 'rgba(255,255,255,0.05)', sm: 'transparent' },
                    backdropFilter: { xs: 'blur(10px)', sm: 'none' },
                    '&:hover': {
                      /*transform: 'translateY(-2px)',*/
                      bgcolor: { xs: 'rgba(255,255,255,0.05)', sm: 'transparent' },
                    }
                  }}
                >
                  <Box sx={{
                    bgcolor: `${item.color}33`,
                    p: 1.5,
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                    <item.icon sx={{ fontSize: { xs: 24, sm: 28 }, color: item.color }} />
                  </Box>
                  <Typography 
                    sx={{ 
                      fontSize: { xs: '1rem', sm: '1.1rem' },
                      fontWeight: 500,
                      letterSpacing: '0.5px',
                      whiteSpace: { sm: 'nowrap' }
                    }}
                  >
                    {item.text}
                  </Typography>
                </Box>
              ))}
            </Stack>

            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              justifyContent="center"
              sx={{
                '& > *': {
                  width: { xs: '100%', sm: 'auto' }
                }
              }}
            >
              <Button
                variant="contained"
                size="large"
                endIcon={<ArrowForward />}
                onClick={handlePartnershipClick}
                sx={{ 
                  bgcolor: 'white',
                  color: '#6B46C1',
                  px: { xs: 4, sm: 6 },
                  py: 2,
                  fontSize: { xs: '1rem', sm: '1.1rem' },
                  fontWeight: 600,
                  borderRadius: '50px',
                  boxShadow: '0 8px 20px rgba(0,0,0,0.12)',
                  '&:hover': { 
                    bgcolor: 'rgba(255,255,255,0.95)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 12px 24px rgba(0,0,0,0.15)'
                  },
                  transition: 'all 0.2s ease'
                }}
              >
                Become a Partner
              </Button>
              {/* <Button
                variant="outlined"
                size="large"
                sx={{
                  borderColor: 'rgba(255,255,255,0.6)',
                  borderWidth: 2,
                  color: 'white',
                  px: { xs: 4, sm: 6 },
                  py: 2,
                  fontSize: { xs: '1rem', sm: '1.1rem' },
                  fontWeight: 500,
                  borderRadius: '50px',
                  backdropFilter: 'blur(10px)',
                  '&:hover': {
                    borderColor: 'white',
                    bgcolor: 'rgba(255,255,255,0.1)',
                    transform: 'translateY(-2px)'
                  },
                  transition: 'all 0.2s ease'
                }}
              >
                Learn More
              </Button> */}
            </Stack>
          </Box>
        </Container>
      </Box>

      {/* Partnership Types */}
      <Container 
        maxWidth="xl" 
        sx={{ 
          py: { xs: 6, md: 10, xl: 12 },
          px: { xs: 2, sm: 3, md: 4, xl: 8 }
        }}
      >
        <Grid container spacing={{ xs: 3, md: 4 }}>
          {partnershipTypes.map((type, index) => (
            <Grid item xs={12} md={4} key={type.title}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 3, md: 5 },
                  height: '100%',
                  borderRadius: 4,
                  bgcolor: 'rgba(255,255,255,0.98)',
                  backdropFilter: 'blur(20px)',
                  border: '1px solid rgba(107, 70, 193, 0.1)',
                  transition: 'all 0.4s ease',
                  animation: `${fadeIn} ${0.4 + index * 0.2}s ease-out`,
                  '&:hover': {
                    transform: 'translateY(-6px)',
                    boxShadow: '0 25px 50px rgba(107, 70, 193, 0.12)',
                    borderColor: type.color
                  }
                }}
              >
                <Box 
                  sx={{ 
                    mb: 3,
                    display: 'inline-flex',
                    p: { xs: 1.5, md: 2 },
                    borderRadius: 3,
                    bgcolor: `${type.color}15`,
                    color: type.color,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      bgcolor: `${type.color}25`,
                      transform: 'scale(1.1)',
                    }
                  }}
                >
                  <type.icon sx={{ fontSize: { xs: 36, md: 44 } }} />
                </Box>
                <Typography 
                  variant="h4" 
                  gutterBottom
                  sx={{ 
                    fontWeight: 700,
                    mb: 2,
                    color: 'text.primary',
                    fontSize: { xs: '1.5rem', md: '1.75rem' }
                  }}
                >
                  {type.title}
                </Typography>
                <Typography 
                  color="text.secondary" 
                  sx={{ 
                    mb: 3,
                    lineHeight: 1.6,
                    fontSize: { xs: '1rem', md: '1.1rem' }
                  }}
                >
                  {type.description}
                </Typography>
                <Stack spacing={2}>
                  {type.benefits.map(benefit => (
                    <Typography 
                      key={benefit}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        color: 'text.primary',
                        fontWeight: 500,
                        fontSize: { xs: '0.95rem', md: '1.05rem' },
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          color: type.color,
                          transform: 'translateX(4px)'
                        },
                        '&:before': {
                          content: '"→"',
                          color: type.color,
                          mr: 2,
                          fontWeight: 'bold',
                          fontSize: '1.2rem'
                        }
                      }}
                    >
                      {benefit}
                    </Typography>
                  ))}
                </Stack>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>
    </PublicLayout>
  );
};

export default PartnershipsPage;